import React, { useEffect, useMemo, useCallback, useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  TextField,
  Divider,
  Button,
  CircularProgress,
  Stack,
} from '@mui/material';
import { generateContent } from '../../services/AIService';
import {
  assembleTemporaryDiscontinuationSection712,
  buildTemporaryDiscontinuation712Prompt,
  DEFAULT_TEMPORARY_DISCONTINUATION_DECISIONS,
  TEMP_DISC_ALLOWED,
  TEMP_DISC_NOT_DESCRIBED_MODE,
  TEMP_DISC_NA_STATEMENT,
} from '../../utils/temporaryDiscontinuationSection712';

const DECISIONS_PATH = 'discontinuation_rules.temporary_discontinuation_decisions';
const OUTPUT_PATH = 'discontinuation_rules.temporary_discontinuation_trial_intervention';

const TemporaryDiscontinuationSection712Panel = ({
  values,
  setFieldValue,
  isFormDisabled,
}) => {
  const decisions =
    values?.discontinuation_rules?.temporary_discontinuation_decisions ||
    DEFAULT_TEMPORARY_DISCONTINUATION_DECISIONS;
  const [generateBusy, setGenerateBusy] = useState(false);
  const [generateError, setGenerateError] = useState(null);

  const setDecision = useCallback(
    (field, value) => {
      setFieldValue(`${DECISIONS_PATH}.${field}`, value);
    },
    [setFieldValue]
  );

  const assemblyInput = useMemo(
    () => ({
      discontinuation_rules: {
        temporary_discontinuation_decisions: decisions,
        temporary_discontinuation_trial_intervention:
          values?.discontinuation_rules?.temporary_discontinuation_trial_intervention || '',
      },
    }),
    [
      decisions.temporary_discontinuation_allowed,
      decisions.not_described_output_mode,
      decisions.protocol_source_excerpts,
      values?.discontinuation_rules?.temporary_discontinuation_trial_intervention,
    ]
  );

  const assembly = useMemo(
    () => assembleTemporaryDiscontinuationSection712(assemblyInput),
    [assemblyInput]
  );

  // Sync deterministic No pathways into the output field
  useEffect(() => {
    if (decisions.temporary_discontinuation_allowed !== TEMP_DISC_ALLOWED.NO) return;
    if (!decisions.not_described_output_mode) return;

    if (decisions.not_described_output_mode === TEMP_DISC_NOT_DESCRIBED_MODE.OMIT) {
      const current = values?.discontinuation_rules?.temporary_discontinuation_trial_intervention;
      if (current) {
        setFieldValue(OUTPUT_PATH, '');
      }
      return;
    }
    if (
      values?.discontinuation_rules?.temporary_discontinuation_trial_intervention !==
      TEMP_DISC_NA_STATEMENT
    ) {
      setFieldValue(OUTPUT_PATH, TEMP_DISC_NA_STATEMENT);
    }
  }, [
    decisions.temporary_discontinuation_allowed,
    decisions.not_described_output_mode,
    setFieldValue,
    values?.discontinuation_rules?.temporary_discontinuation_trial_intervention,
  ]);

  const handleGenerate = async () => {
    const excerpts = String(decisions.protocol_source_excerpts || '').trim();
    if (!excerpts) {
      setGenerateError('Paste protocol source excerpts first.');
      return;
    }
    setGenerateBusy(true);
    setGenerateError(null);
    try {
      const prompt = buildTemporaryDiscontinuation712Prompt({ protocolExcerpts: excerpts });
      const text = await generateContent(prompt, '', '', 'temp_disc_712', '7.1.2');
      const body = String(text || '')
        .trim()
        .replace(/^7\.1\.2[^\n]*\n+/i, '')
        .trim();
      if (!body) {
        throw new Error('Empty response from generation.');
      }
      setFieldValue(OUTPUT_PATH, body);
    } catch (e) {
      setGenerateError(e.message || 'Could not generate Section 7.1.2 from protocol source.');
    } finally {
      setGenerateBusy(false);
    }
  };

  const showNotDescribed =
    decisions.temporary_discontinuation_allowed === TEMP_DISC_ALLOWED.NO;
  const showYesPath = decisions.temporary_discontinuation_allowed === TEMP_DISC_ALLOWED.YES;
  const outputText =
    values?.discontinuation_rules?.temporary_discontinuation_trial_intervention || '';

  return (
    <Box sx={{ mb: 3, p: 2, border: '1px solid #dbe3eb', borderRadius: 1, bgcolor: '#fafbfc' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
        Section 7.1.2 — Temporary Discontinuation of Trial Intervention
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Decision tree for temporary interruption. When allowed, the narrative is authored{' '}
        <strong>only</strong> from protocol source excerpts — no invented interruption rules,
        durations, restart criteria, or monitoring.
      </Typography>

      {assembly.warnings.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {assembly.warnings.map((w) => (
            <div key={w}>{w}</div>
          ))}
        </Alert>
      )}
      {generateError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGenerateError(null)}>
          {generateError}
        </Alert>
      )}

      <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }} disabled={isFormDisabled}>
        <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
          1. Does the protocol allow temporary discontinuation (interruption) of trial intervention?
        </FormLabel>
        <RadioGroup
          value={decisions.temporary_discontinuation_allowed || ''}
          onChange={(e) => {
            const next = e.target.value;
            setDecision('temporary_discontinuation_allowed', next);
            if (next === TEMP_DISC_ALLOWED.YES) {
              setDecision('not_described_output_mode', '');
              setFieldValue(OUTPUT_PATH, '');
            } else if (next === TEMP_DISC_ALLOWED.NO) {
              setDecision('protocol_source_excerpts', '');
              setFieldValue(OUTPUT_PATH, '');
            }
          }}
        >
          <FormControlLabel value={TEMP_DISC_ALLOWED.YES} control={<Radio size="small" />} label="Yes" />
          <FormControlLabel value={TEMP_DISC_ALLOWED.NO} control={<Radio size="small" />} label="No" />
        </RadioGroup>
      </FormControl>

      {showNotDescribed && (
        <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }} disabled={isFormDisabled}>
          <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
            2. Temporary discontinuation is not described — output mode
          </FormLabel>
          <RadioGroup
            value={decisions.not_described_output_mode || ''}
            onChange={(e) => setDecision('not_described_output_mode', e.target.value)}
          >
            <FormControlLabel
              value={TEMP_DISC_NOT_DESCRIBED_MODE.STATEMENT}
              control={<Radio size="small" />}
              label={`Option A — Generate heading with: “${TEMP_DISC_NA_STATEMENT}”`}
            />
            <FormControlLabel
              value={TEMP_DISC_NOT_DESCRIBED_MODE.OMIT}
              control={<Radio size="small" />}
              label="Option B — Omit Section 7.1.2 entirely"
            />
          </RadioGroup>
        </FormControl>
      )}

      {showYesPath && (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            multiline
            minRows={6}
            label="Protocol source excerpts (sole source of truth)"
            placeholder="Paste the exact protocol text defining temporary discontinuation / interruption, restart criteria, assessments, and related cross-references."
            value={decisions.protocol_source_excerpts || ''}
            onChange={(e) => {
              setDecision('protocol_source_excerpts', e.target.value);
            }}
            disabled={isFormDisabled}
            helperText="Only this text may be used. Do not invent criteria missing from the source."
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
            <Button
              size="small"
              variant="contained"
              onClick={handleGenerate}
              disabled={
                isFormDisabled ||
                generateBusy ||
                !String(decisions.protocol_source_excerpts || '').trim()
              }
            >
              {generateBusy ? <CircularProgress size={18} /> : 'Generate 7.1.2 from Protocol Source'}
            </Button>
          </Stack>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Assembled Section 7.1.2 preview
      </Typography>
      {assembly.omitSection ? (
        <Alert severity="warning">
          Section 7.1.2 will be omitted from protocol generation (Option B).
        </Alert>
      ) : (
        <Box
          sx={{
            p: 1.5,
            bgcolor: '#fff',
            border: '1px solid #e5eaf0',
            borderRadius: 1,
            whiteSpace: 'pre-wrap',
            fontSize: 13,
            maxHeight: 280,
            overflow: 'auto',
          }}
        >
          {outputText || (
            <Typography variant="body2" color="text.secondary">
              Complete the questions above to assemble Section 7.1.2.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TemporaryDiscontinuationSection712Panel;
